import { useGetResidentsIndexQuery } from "app/services/api/residentsApi";
import { ResidentsIndexResponse } from "app/services/api/types/residents/responses/ResidentsIndexResponse";
import { RootState } from "app/services/store";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import usePagination from "ui/molecules/pagination/usePagination";
import { useResidentsFilterContext } from "app/features/residents/context/ResidentsFilterContext";

export function useResidentListingWithOffline() {
  const { filters, search } = useResidentsFilterContext();

  // Convert filters to API params format
  const params = useMemo(() => {
    const apiParams: Record<string, string> = {};

    if (search) apiParams.search = search;
    if (filters.location_id)
      apiParams.location_id = filters.location_id.toString();
    if (filters.floor_id) {
      apiParams.floor_id = Array.isArray(filters.floor_id)
        ? filters.floor_id.join(',')
        : filters.floor_id.toString();
    }
    if (filters.status) apiParams.status = filters.status;
    if (filters.gender) apiParams.gender = filters.gender;
    if (filters.status_flag_id)
      apiParams.status_flag_id = filters.status_flag_id.toString();
    if (filters.admission_start_date_from)
      apiParams.admission_start_date_from = filters.admission_start_date_from;
    if (filters.admission_start_date_to)
      apiParams.admission_start_date_to = filters.admission_start_date_to;
    if (filters.admission_end_date_from)
      apiParams.admission_end_date_from = filters.admission_end_date_from;
    if (filters.admission_end_date_to)
      apiParams.admission_end_date_to = filters.admission_end_date_to;

    return apiParams;
  }, [filters, search]);

  const { currentPage, handlePageChange } = usePagination();

  const {
    data: onlineData,
    isFetching,
    isError,
  } = useGetResidentsIndexQuery({
    page: currentPage,
    filter: params,
  });

  const offlineData =
    useSelector(
      (state: RootState) => state.residents?.residents[currentPage],
    ) ?? [];

  const isOnline = useSelector(
    (state: RootState) => state.network?.networkState?.isInternetReachable,
  );

  const data = useMemo(() => {
    if (isFetching || (isOnline && onlineData)) return onlineData;
    if (isError && offlineData) return offlineData as ResidentsIndexResponse;
    return onlineData;
  }, [onlineData, offlineData, isOnline, isFetching, isError]);
  return {
    data,
    isFetching,
    handlePageChange,
    currentPage,
  };
}
